import { useEffect } from "react";
import { View, Platform } from "react-native";

import { useModalComponent } from "../../context/Platform/hooks";
import { useTheme } from "../../utils";

import type { StyleProp, ViewStyle } from "react-native";
import type { ModalNativeProps } from "../ModalBase";
import type { DialogBaseProps } from "./DialogBase";

export interface DialogProps extends DialogBaseProps {
  style?: StyleProp<ViewStyle>;

  /**
   * @platform ios, android
   */
  avoidKeyboard?: ModalNativeProps["avoidKeyboard"];

  /**
   * @platform ios, android
   */
  containerStyle?: StyleProp<ViewStyle>;

  /**
   * @platform web
   */
  closeOnEsc?: boolean;
}

export function Dialog({
  avoidKeyboard = true,
  backdropOpacity,
  visible,
  children,
  closeOnEsc = true,
  closeOnOutsidePress = true,
  onClose,
  style,
  containerStyle,
  titleID,
  descriptionID,
  testID,
}: DialogProps) {
  const Modal = useModalComponent();
  const { shadows } = useTheme();

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    if (Platform.OS === "web" && closeOnEsc && visible) {
      document.addEventListener("keyup", handleEscape);
    }

    return () => {
      if (Platform.OS === "web") {
        document.removeEventListener("keyup", handleEscape);
      }
    };
  }, [onClose, closeOnEsc, visible]);

  if (Platform.OS === "web") {
    return (
      <Modal
        visible={visible}
        onBackdropPress={closeOnOutsidePress ? onClose : undefined}
        backdropOpacity={backdropOpacity}
        style={[
          {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
          },

          style,
        ]}
      >
        <div
          style={{
            justifySelf: "center",
            alignSelf: "center",
            ...shadows.dialog,
          }}
          role="dialog"
          aria-labelledby={titleID}
          aria-describedby={descriptionID}
          data-testid={testID}
        >
          {children}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      visible={visible}
      onBackButtonPress={onClose}
      onBackdropPress={onClose}
      backdropOpacity={backdropOpacity}
      avoidKeyboard={avoidKeyboard}
      testID={testID}
      style={style}
    >
      <View style={[{ maxWidth: 328 }, shadows.lg, containerStyle]}>
        {children}
      </View>
    </Modal>
  );
}
